/**
 * Shared data hook for Discover variants.
 *
 * Centralizes the sections' API calls so each variant only worries about
 * presentation. The search lives in `search/useDiscoverSearch` (#2290).
 *
 * Each list is asked for only while a section the page draws needs it
 * (#2292): a hidden section costs no request, and showing it asks then.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getEditorsPicks, getTrendingModels } from '@/api/models';
import { getRecentItems, getFavorites } from '@/api/profiles';
import { shelfFavorites } from './favorites';
import type { Model } from '@/types';
import { descriptionLabel, getLabel, type LabelValue } from '@/utils/label';

export interface DiscoverItem {
  _id: string;
  metadata?: {
    label?: string | { value?: string };
    description?: string | { value?: string };
    desc?: { value?: string };
    tags?: string[];
    createdBy?: string;
    updatedBy?: string;
    /** Real API date keys (ISO strings) returned by the search/list endpoints. */
    dateCreated?: string;
    lastUpdated?: string;
    /** Legacy / alternate key names — kept as fallbacks. */
    modified?: string | number;
    created?: string | number;
    /** Extension strings (`IconUrl:<url>` names a functional's icon). */
    ext?: string[];
  };
  $owner?: string;
  $icon?: string;
  /** A favorite's reference and URI, which tell its kind apart (see favorites.ts). */
  $reference?: string;
  $uri?: string;
  /** A document favorite's content type. */
  contentType?: string;
}

export type SectionStatus = 'loading' | 'ready' | 'error';

/**
 * One Discover list. Each loads on its own, so a slow or failing call leaves
 * the other sections alone. `items` is empty unless `status` is 'ready'.
 */
export interface DiscoverSection<T> {
  status: SectionStatus;
  items: T[];
  /** Asks again after an error. */
  retry: () => void;
}

export interface UseDiscoverData {
  trending: DiscoverSection<Model>;
  picks: DiscoverSection<Model>;
  recent: DiscoverSection<DiscoverItem>;
  favorites: DiscoverSection<DiscoverItem>;
}

const FAVORITES_LIMIT = 50;

// Module-level so each section's effect runs once per attempt, not per render.
const loadTrending = () => getTrendingModels({ limit: 24 });
const loadPicks = () => getEditorsPicks();
const loadRecent = () => getRecentItems() as Promise<DiscoverItem[]>;
// Every kind, newest first (#2293); enough for a full shelf under any kind filter.
// Documents are dropped: nothing in the app can favorite one.
const loadFavorites = () =>
  (getFavorites('*', FAVORITES_LIMIT) as Promise<DiscoverItem[]>).then(shelfFavorites);

/** Which lists to load: those a drawn section needs. Every list when not given. */
export type DiscoverNeeds = Record<keyof UseDiscoverData, boolean>;

const ALL: DiscoverNeeds = { trending: true, picks: true, recent: true, favorites: true };

/**
 * Loads one section once it is `needed`: one request, another only when
 * `retry` is called. An answer it already has is kept while it is not
 * needed, so hiding and showing a section asks nothing more. A response that
 * arrives after unmount, after it stopped being needed, or after a newer
 * attempt has started, is dropped.
 */
function useSection<T>(load: () => Promise<T[]>, needed: boolean): DiscoverSection<T> {
  // `attempt` is the answer's: an attempt with no answer yet is still to load.
  const [state, setState] = useState<{ status: SectionStatus; items: T[]; attempt: number }>(
    { status: 'loading', items: [], attempt: -1 },
  );
  const [attempt, setAttempt] = useState(0);
  const answered = state.attempt === attempt;

  useEffect(() => {
    if (!needed || answered) return;
    let alive = true;
    load().then(
      (items) => { if (alive) setState({ status: 'ready', items: Array.isArray(items) ? items : [], attempt }); },
      () => { if (alive) setState({ status: 'error', items: [], attempt }); },
    );
    return () => { alive = false; };
  }, [load, attempt, needed, answered]);

  const retry = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading', items: [] }));
    setAttempt((a) => a + 1);
  }, []);

  return useMemo(() => ({ status: state.status, items: state.items, retry }), [state, retry]);
}

export function useDiscoverData(needs: DiscoverNeeds = ALL): UseDiscoverData {
  const trending = useSection(loadTrending, needs.trending);
  const picks = useSection(loadPicks, needs.picks);
  const recent = useSection(loadRecent, needs.recent);
  const favorites = useSection(loadFavorites, needs.favorites);

  return { trending, picks, recent, favorites };
}

/** Normalize label from either string or {value} object. */
export function labelOf(item: DiscoverItem): string {
  const l = item.metadata?.label;
  if (!l) return 'Untitled';
  if (typeof l === 'string') return l;
  if (typeof l === 'object' && 'value' in l) return String(l.value || 'Untitled');
  return 'Untitled';
}

export function descOf(item: DiscoverItem): string {
  return getLabel(descLabelOf(item));
}

/**
 * The stored label behind `labelOf`, for a place that DISPLAYS the name with
 * its classification marking (`MarkedText`). `labelOf` stays for attributes,
 * alt text and cover art.
 */
export function nameLabelOf(item: DiscoverItem): LabelValue {
  return item.metadata?.label as LabelValue;
}

/** The description copy `descOf` reads: the one rule in utils/label. */
export function descLabelOf(item: DiscoverItem): LabelValue {
  return descriptionLabel(item.metadata);
}

export function tagsOf(item: DiscoverItem): string[] {
  return item.metadata?.tags || [];
}

/** Creation timestamp (ISO/epoch) — prefers the real `dateCreated` key. */
export function createdAt(item: DiscoverItem): string | undefined {
  const v = item.metadata?.dateCreated ?? item.metadata?.created;
  return v != null ? String(v) : undefined;
}

/** Last-updated timestamp (ISO/epoch) — prefers the real `lastUpdated` key. */
export function updatedAt(item: DiscoverItem): string | undefined {
  const v = item.metadata?.lastUpdated ?? item.metadata?.modified;
  return v != null ? String(v) : undefined;
}

/** Format an ISO/epoch date as e.g. "May 18, 2026"; returns '' when unparseable. */
export function formatShortDate(value?: string | number): string {
  if (value == null || value === '') return '';
  const d = new Date(typeof value === 'number' ? value : String(value));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
